import pickBy from 'lodash/pickBy'
import { Action } from 'redux'
import * as actions from './actions'
import type { ChannelSidePanel } from './actions'
import { openCachedTimeline } from './offline'
import type { IChannelDetail, IChannelsState, IChannelTimeline } from './reducer'

/**
 * Split view for AIM, layered over the channels reducer: conversations open beside the
 * main one, each with its own timeline and detail.
 *
 * A pane is reduced by running the whole reducer over a view of the state in which that
 * pane is the active conversation, and keeping only its timeline, its detail and the
 * outbox. Every per-conversation rule — paging, sends, live events, deep-link windows —
 * therefore applies to a pane exactly as it does to the main conversation, with no copy
 * of it here. Everything else the view produces is discarded: the main run owns it.
 *
 * A record page's embedded conversation (`embeddedPanes`) is reduced the same way but
 * drawn only by that page, so AIM's own surface can hold the main conversation beside it.
 */

/** Conversations on screen at once, the main one included. */
export const MAX_OPEN_CONVERSATIONS = 5
export const MAX_SPLIT_PANES = MAX_OPEN_CONVERSATIONS - 1

export interface ISplitPane {
  channelId: string
  detail: IChannelDetail | null
  timeline: IChannelTimeline
}

type SplitAction = Action & { data?: unknown }
type ChannelsReducer<A extends Action> = (
  state: IChannelsState | undefined,
  action: A,
) => IChannelsState

/** A record page's own conversation; its side panel is its own, not the main surface's. */
export interface IEmbeddedPane extends ISplitPane {
  sidePanel: ChannelSidePanel
}

export const emptyTimeline = (channelId: string | null): IChannelTimeline => ({
  channelId,
  items: [],
  hasMore: false,
  nextCursor: null,
  nextCursorId: null,
  hasNewer: false,
  isLoading: false,
  isLoadingMore: false,
  hasFailed: false,
})

/** A pane can hold a person's homepage rather than a conversation, under an id of this form. */
const PERSON_PANE_PREFIX = 'person:'
export const personPaneId = (userId: string) => `${PERSON_PANE_PREFIX}${userId}`
export const personOfPane = (paneId: string): string | null =>
  paneId.startsWith(PERSON_PANE_PREFIX) ? paneId.slice(PERSON_PANE_PREFIX.length) : null

export const isOpenInSplitPane = (
  state: Pick<IChannelsState, 'splitPanes'>,
  channelId: string,
): boolean => state.splitPanes.some((pane) => pane.channelId === channelId)

/** Open on screen other than as the main conversation: in a split pane or on a record's page. */
export const isOpenBesideMain = (
  state: Pick<IChannelsState, 'splitPanes' | 'embeddedPanes'>,
  channelId: string,
): boolean =>
  isOpenInSplitPane(state, channelId) ||
  state.embeddedPanes.some((pane) => pane.channelId === channelId)

/** `index` is a slot between the panes as they stand, so moving a pane right lands one short. */
const openPane = (state: IChannelsState, channelId: string, index: number): IChannelsState => {
  if (channelId === state.activeChannelId) return state
  const existingIndex = state.splitPanes.findIndex((pane) => pane.channelId === channelId)
  const existing = state.splitPanes[existingIndex]
  const others = state.splitPanes.filter((pane) => pane.channelId !== channelId)
  if (!existing && others.length >= MAX_SPLIT_PANES) return state

  let next = state
  let pane = existing
  if (!pane && personOfPane(channelId)) {
    pane = { channelId, detail: null, timeline: emptyTimeline(channelId) }
  } else if (!pane) {
    const { timeline, outbox } = openCachedTimeline(state, channelId, {
      ...emptyTimeline(channelId),
      isLoading: true,
    })
    pane = { channelId, detail: state.channelDetailCache[channelId] ?? null, timeline }
    next = { ...state, outbox }
  }
  const target = existing && existingIndex < index ? index - 1 : index
  const slot = Math.max(0, Math.min(target, others.length))
  return { ...next, splitPanes: [...others.slice(0, slot), pane, ...others.slice(slot)] }
}

const openEmbedded = (state: IChannelsState, channelId: string): IChannelsState => {
  if (state.embeddedPanes.some((pane) => pane.channelId === channelId)) return state
  const isMain = state.activeChannelId === channelId
  const { timeline, outbox } = isMain
    ? { timeline: state.messages, outbox: state.outbox }
    : openCachedTimeline(state, channelId, { ...emptyTimeline(channelId), isLoading: true })
  const detail = isMain ? state.activeChannel : (state.channelDetailCache[channelId] ?? null)
  const pane: IEmbeddedPane = { channelId, detail, timeline, sidePanel: null }
  return { ...state, outbox, embeddedPanes: [...state.embeddedPanes, pane] }
}

const updateEmbedded = (
  state: IChannelsState,
  channelId: string,
  update: (pane: IEmbeddedPane) => IEmbeddedPane | null,
): IChannelsState => {
  if (!state.embeddedPanes.some((pane) => pane.channelId === channelId)) return state
  const embeddedPanes = state.embeddedPanes.flatMap((pane) => {
    if (pane.channelId !== channelId) return [pane]
    const next = update(pane)
    return next ? [next] : []
  })
  return { ...state, embeddedPanes }
}

/**
 * Switching the main conversation to one already in a pane swaps the two, so neither
 * closes and the pane's loaded history comes with it.
 *
 * The core reducer drops Arc traces and typing indicators on every switch; the ones
 * belonging to a conversation still on screen are put back.
 */
const followActiveChannel = (prev: IChannelsState, next: IChannelsState): IChannelsState => {
  if (next === prev || (!prev.splitPanes.length && !prev.embeddedPanes.length)) return next
  const index = prev.splitPanes.findIndex((pane) => pane.channelId === next.activeChannelId)

  let state = next
  if (index !== -1) {
    const arriving = prev.splitPanes[index]
    const leaving: ISplitPane | null = prev.activeChannelId
      ? { channelId: prev.activeChannelId, detail: prev.activeChannel, timeline: prev.messages }
      : null
    const splitPanes = prev.splitPanes.flatMap((pane, at) =>
      at !== index ? [pane] : leaving ? [leaving] : [],
    )
    state = {
      ...next,
      splitPanes,
      messages: arriving.timeline,
      activeChannel: arriving.detail ?? next.activeChannel,
    }
  }

  const onScreen = new Set([
    ...(state.activeChannelId ? [state.activeChannelId] : []),
    ...state.splitPanes.map((pane) => pane.channelId),
    ...state.embeddedPanes.map((pane) => pane.channelId),
  ])
  return {
    ...state,
    arcProgress: {
      ...state.arcProgress,
      ...pickBy(prev.arcProgress, (progress) => onScreen.has(progress.channelId ?? '')),
    },
    typingUsers: {
      ...state.typingUsers,
      ...pickBy(prev.typingUsers, (_, channelId) => onScreen.has(channelId)),
    },
  }
}

const reducePaneList = <A extends Action, P extends ISplitPane>(
  reducer: ChannelsReducer<A>,
  state: IChannelsState,
  action: A,
  panes: P[],
): { shared: IChannelsState; panes: P[] } => {
  let shared = state
  let hasChanged = false
  const nextPanes = panes.map((pane) => {
    if (personOfPane(pane.channelId)) return pane
    const view = reducer(
      {
        ...shared,
        activeChannelId: pane.channelId,
        activeChannel: pane.detail,
        messages: pane.timeline,
      },
      action,
    )
    // Threaded through, so an unsent message the server turns out to hold leaves the outbox.
    if (view.outbox !== shared.outbox) shared = { ...shared, outbox: view.outbox }
    if (view.messages === pane.timeline && view.activeChannel === pane.detail) return pane
    hasChanged = true
    return { ...pane, timeline: view.messages, detail: view.activeChannel }
  })
  return { shared, panes: hasChanged ? nextPanes : panes }
}

const reducePanes = <A extends Action>(
  reducer: ChannelsReducer<A>,
  state: IChannelsState,
  action: A,
): IChannelsState => {
  const split = reducePaneList(reducer, state, action, state.splitPanes)
  const embedded = reducePaneList(reducer, split.shared, action, state.embeddedPanes)
  if (split.panes === state.splitPanes && embedded.panes === state.embeddedPanes) {
    return embedded.shared
  }
  return { ...embedded.shared, splitPanes: split.panes, embeddedPanes: embedded.panes }
}

export const withSplitPanes =
  <A extends Action>(reducer: ChannelsReducer<A>) =>
  (state: IChannelsState | undefined, action: A): IChannelsState => {
    const next = reducer(state, action)
    if (!state) return next
    const { data } = action as SplitAction

    switch (action.type) {
      case actions.OPEN_SPLIT_PANE: {
        const { channelId, index } = data as { channelId: string; index: number }
        return openPane(next, channelId, index)
      }
      case actions.CLOSE_SPLIT_PANE: {
        const { channelId } = data as { channelId: string }
        if (!isOpenInSplitPane(next, channelId)) return next
        return {
          ...next,
          splitPanes: next.splitPanes.filter((pane) => pane.channelId !== channelId),
        }
      }
      case actions.CLOSE_SPLIT_PANES:
        return next.splitPanes.length ? { ...next, splitPanes: [] } : next
      case actions.OPEN_EMBEDDED_CONVERSATION:
        return openEmbedded(next, (data as { channelId: string }).channelId)
      case actions.CLOSE_EMBEDDED_CONVERSATION:
        return updateEmbedded(next, (data as { channelId: string }).channelId, () => null)
      case actions.SET_EMBEDDED_SIDE_PANEL: {
        const { channelId, panel } = data as { channelId: string; panel: ChannelSidePanel }
        return updateEmbedded(next, channelId, (pane) => ({ ...pane, sidePanel: panel }))
      }
      case actions.SET_ACTIVE_CHANNEL:
        return followActiveChannel(state, next)
      default:
        return next.splitPanes.length || next.embeddedPanes.length
          ? reducePanes(reducer, next, action)
          : next
    }
  }
